/**
 * Smooth <details> accordions (Gaucho House): buy box accordions (blocks/gh-product-accordion.liquid)
 * and the mobile footer columns (sections/gh-footer.liquid).
 *
 * Markup: <details data-gh-accordion><summary>…</summary>…content…</details>
 *
 * A click on the summary (mouse, Enter or Space) is intercepted and the <details> height is
 * animated with the Web Animations API, from the summary's height to the full height and back,
 * while the content fades. `open` is set before opening and only removed once closing ends, so it
 * stays a native details/summary for keyboards and assistive tech. `data-gh-accordion-state`
 * ("opening" / "closing") lets CSS turn the +/− icon at the start of each animation.
 *
 * Clicks already handled elsewhere (event.defaultPrevented, e.g. the footer columns on desktop)
 * are left alone. With prefers-reduced-motion the details toggles natively, without animation.
 */
const DURATION = 300;
const EASING = 'cubic-bezier(0.2, 0.7, 0.2, 1)';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {WeakMap<HTMLDetailsElement, Animation>} */
const running = new WeakMap();

/** @param {HTMLDetailsElement} details */
function closedHeight(details) {
  const summary = details.querySelector(':scope > summary');
  const style = getComputedStyle(details);
  const frame =
    parseFloat(style.paddingTop) +
    parseFloat(style.paddingBottom) +
    parseFloat(style.borderTopWidth) +
    parseFloat(style.borderBottomWidth);

  return (summary?.getBoundingClientRect().height ?? 0) + frame;
}

/** @param {HTMLDetailsElement} details */
function contentElements(details) {
  return [...details.children].filter((child) => child.tagName !== 'SUMMARY');
}

/**
 * @param {HTMLDetailsElement} details
 * @param {boolean} opening
 */
function animate(details, opening) {
  const startHeight = details.getBoundingClientRect().height;
  const wasRunning = running.has(details);
  running.get(details)?.cancel();

  details.dataset.ghAccordionState = opening ? 'opening' : 'closing';
  if (opening) details.open = true;

  // Natural height once open (the previous animation is cancelled, so nothing constrains it)
  const endHeight = opening ? details.getBoundingClientRect().height : closedHeight(details);
  const content = contentElements(details);
  const startOpacity = content.length ? Number(getComputedStyle(content[0]).opacity) : 1;
  for (const element of content) {
    for (const fade of element.getAnimations()) fade.cancel();
  }

  details.style.overflow = 'hidden';

  const animation = details.animate(
    { height: [`${startHeight}px`, `${endHeight}px`] },
    { duration: DURATION, easing: EASING }
  );
  for (const element of content) {
    element.animate(
      { opacity: opening ? [wasRunning ? startOpacity : 0, 1] : [startOpacity, 0] },
      { duration: DURATION, easing: EASING, fill: 'forwards' }
    );
  }
  running.set(details, animation);

  animation.onfinish = () => {
    running.delete(details);
    if (!opening) details.open = false;
    details.style.overflow = '';
    delete details.dataset.ghAccordionState;
    for (const element of content) {
      for (const fade of element.getAnimations()) fade.cancel();
    }
  };
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || reducedMotion.matches) return;
  if (!(event.target instanceof Element)) return;

  const summary = event.target.closest('summary');
  const details = summary?.parentElement;
  if (!(details instanceof HTMLDetailsElement) || !details.hasAttribute('data-gh-accordion')) return;

  event.preventDefault();

  // Closing while it opens (or the other way round) reverses from the current height
  const opening = details.dataset.ghAccordionState
    ? details.dataset.ghAccordionState === 'closing'
    : !details.open;

  animate(details, opening);
});
